import React, { useState, useMemo } from 'react';

const CHART_WIDTH = 800;
const CHART_HEIGHT = 220;
const CHART_PADDING = { top: 20, right: 30, bottom: 30, left: 50 };

/**
 * Responsive, zero-dependency SVG trend visualization
 * Switchable between Revenue, Orders, Units, and AOV
 */
export const TrendChart = ({ timeline = [], defaultMetric = 'revenue' }) => {
  const [activeMetric, setActiveMetric] = useState(defaultMetric);
  const [hoveredPoint, setHoveredPoint] = useState(null);

  const metricsConfig = {
    revenue: { label: 'Revenue', shortLabel: 'Revenue', prefix: '£', key: 'revenue', color: '#b28a4c', gradient: 'rgba(178, 138, 76, 0.15)' },
    orders: { label: 'Orders', shortLabel: 'Orders', prefix: '', key: 'orders', color: '#2563eb', gradient: 'rgba(37, 99, 235, 0.15)' },
    units: { label: 'Units Sold', shortLabel: 'Units', prefix: '', key: 'units', color: '#7c3aed', gradient: 'rgba(124, 58, 237, 0.15)' },
    aov: { label: 'Average Order Value', shortLabel: 'AOV', prefix: '£', key: 'aov', color: '#059669', gradient: 'rgba(5, 150, 105, 0.15)' },
  };

  const currentConfig = metricsConfig[activeMetric] || metricsConfig.revenue;
  const width = CHART_WIDTH;
  const height = CHART_HEIGHT;
  const padding = CHART_PADDING;

  const dataPoints = useMemo(() => {
    if (!timeline || timeline.length === 0) return [];
    return timeline.map(item => ({
      date: item.date,
      val: Number(item[currentConfig.key] || 0)
    }));
  }, [timeline, currentConfig.key]);

  const { maxVal, minVal, points, pathD, areaD } = useMemo(() => {
    if (dataPoints.length === 0) {
      return { maxVal: 100, minVal: 0, points: [], pathD: '', areaD: '' };
    }

    const values = dataPoints.map(d => d.val);
    let max = Math.max(...values);
    let min = Math.min(...values);

    if (max === min) {
      max = max > 0 ? max * 1.2 : 100;
      min = 0;
    }
    // Buffer
    max = Math.ceil(max * 1.1);
    min = 0;

    const chartW = width - padding.left - padding.right;
    const chartH = height - padding.top - padding.bottom;

    const computedPoints = dataPoints.map((d, index) => {
      const x = padding.left + (index / Math.max(1, dataPoints.length - 1)) * chartW;
      const y = padding.top + chartH - ((d.val - min) / (max - min)) * chartH;
      return { x, y, date: d.date, val: d.val };
    });

    // Build SVG path
    let pD = '';
    let aD = '';
    if (computedPoints.length > 0) {
      pD = `M ${computedPoints[0].x} ${computedPoints[0].y}`;
      computedPoints.slice(1).forEach(pt => {
        pD += ` L ${pt.x} ${pt.y}`;
      });

      const bottomY = padding.top + chartH;
      aD = `${pD} L ${computedPoints[computedPoints.length - 1].x} ${bottomY} L ${computedPoints[0].x} ${bottomY} Z`;
    }

    return { maxVal: max, minVal: min, points: computedPoints, pathD: pD, areaD: aD };
  }, [dataPoints, width, height, padding]);

  // Format date helper
  const formatShortDate = (dStr) => {
    if (!dStr) return '';
    const parts = dStr.split('-');
    if (parts.length === 3) {
      const d = new Date(dStr);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    }
    return dStr;
  };

  return (
    <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm flex flex-col justify-between h-full">
      {/* Header with Metric Switcher */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-base font-semibold text-stone-900">Performance Over Time</h3>
          <p className="text-xs text-stone-500">Daily operational breakdown and activity trajectory</p>
        </div>

        {/* Metric Selector Tabs */}
        <div className="inline-flex bg-stone-100 p-1 rounded-lg text-xs font-medium text-stone-600">
          {Object.entries(metricsConfig).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => {
                setActiveMetric(key);
                setHoveredPoint(null);
              }}
              className={`px-3 py-1.5 rounded-md transition-all ${
                activeMetric === key
                  ? 'bg-white text-stone-900 shadow-sm font-semibold'
                  : 'hover:text-stone-900'
              }`}
            >
              {cfg.shortLabel || cfg.label}
            </button>
          ))}
        </div>
      </div>

      {/* SVG Canvas */}
      <div className="relative w-full aspect-[800/220] select-none">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full h-full overflow-visible"
        >
          <defs>
            <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={currentConfig.color} stopOpacity="0.25" />
              <stop offset="100%" stopColor={currentConfig.color} stopOpacity="0.0" />
            </linearGradient>
          </defs>

          {/* Horizontal Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const y = padding.top + (height - padding.top - padding.bottom) * ratio;
            const val = Math.round(maxVal - ratio * (maxVal - minVal));
            return (
              <g key={ratio}>
                <line
                  x1={padding.left}
                  y1={y}
                  x2={width - padding.right}
                  y2={y}
                  stroke="#f1f5f9"
                  strokeDasharray="4 4"
                />
                <text
                  x={padding.left - 8}
                  y={y + 4}
                  textAnchor="end"
                  className="text-[10px] fill-stone-400 font-mono"
                >
                  {currentConfig.prefix}{val}
                </text>
              </g>
            );
          })}

          {/* Area Fill */}
          {areaD && (
            <path d={areaD} fill="url(#chartGradient)" />
          )}

          {/* Trend Line */}
          {pathD && (
            <path
              d={pathD}
              fill="none"
              stroke={currentConfig.color}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}

          {/* Data Points / Hover Targets */}
          {points.map((pt, i) => (
            <g key={i} className="cursor-pointer">
              {/* Invisible touch/hover target */}
              <circle
                cx={pt.x}
                cy={pt.y}
                r="12"
                fill="transparent"
                onMouseEnter={() => setHoveredPoint(pt)}
                onMouseLeave={() => setHoveredPoint(null)}
              />
              {/* Visible dot if hovered or first/last */}
              {(hoveredPoint?.date === pt.date) && (
                <circle
                  cx={pt.x}
                  cy={pt.y}
                  r="5"
                  fill={currentConfig.color}
                  stroke="#ffffff"
                  strokeWidth="2.5"
                  className="transition-all animate-pulse"
                />
              )}
            </g>
          ))}

          {/* X Axis Labels */}
          {points.length > 0 && (
            <>
              <text
                x={padding.left}
                y={height - 8}
                textAnchor="start"
                className="text-[10px] fill-stone-400"
              >
                {formatShortDate(points[0].date)}
              </text>
              {points.length > 2 && (
                <text
                  x={width / 2}
                  y={height - 8}
                  textAnchor="middle"
                  className="text-[10px] fill-stone-400"
                >
                  {formatShortDate(points[Math.floor(points.length / 2)].date)}
                </text>
              )}
              <text
                x={width - padding.right}
                y={height - 8}
                textAnchor="end"
                className="text-[10px] fill-stone-400"
              >
                {formatShortDate(points[points.length - 1].date)}
              </text>
            </>
          )}
        </svg>

        {/* Floating Tooltip */}
        {hoveredPoint && (
          <div
            style={{
              left: `${(hoveredPoint.x / width) * 100}%`,
              top: `${(hoveredPoint.y / height) * 100}%`
            }}
            className="absolute -translate-x-1/2 -translate-y-12 bg-stone-900 text-white text-xs px-2.5 py-1.5 rounded-md shadow-xl pointer-events-none whitespace-nowrap z-20"
          >
            <div className="text-[10px] text-stone-300">{formatShortDate(hoveredPoint.date)}</div>
            <div className="font-semibold text-white">
              {currentConfig.prefix}{hoveredPoint.val.toLocaleString()} {currentConfig.label}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrendChart;
